/**
 * PlanetTerrainBaker — generates procedural terrain textures + normal maps
 * from the planet's profile seed. Uses simplex-noise + seedrandom for
 * deterministic results.
 *
 * Key features:
 *   - Domain-warped continent shapes for organic coastlines
 *   - Ridged multifractal noise for mountain ridges
 *   - Continent mask separating flat oceans from detailed land
 *   - Slope-based colour blending (exposed rock on steep terrain)
 *   - Temperature/humidity biomes for terran planets (Whittaker)
 *   - Polar ice caps (terran + ice)
 *   - Normal map baking from elevation gradient
 *   - Gas giant latitude bands with turbulence + storms
 *   - LOD-adaptive resolution with partial-region baking
 *   - Cloud texture baking (separate export)
 */

import { createNoise3D, type NoiseFunction3D } from 'simplex-noise';
import seedrandom from 'seedrandom';
import type { PlanetProfileWire } from './state/planetPanelStore';

// ==================== LRU CACHE ====================

/** Simple LRU map — evicts oldest entries when capacity is exceeded. */
class LRUMap<K, V> {
  private map = new Map<K, V>();
  constructor(private maxSize: number) {}

  get(key: K): V | undefined {
    const val = this.map.get(key);
    if (val !== undefined) {
      // Move to end (most recent)
      this.map.delete(key);
      this.map.set(key, val);
    }
    return val;
  }

  set(key: K, value: V): void {
    if (this.map.has(key)) this.map.delete(key);
    this.map.set(key, value);
    // Evict oldest entries beyond capacity
    while (this.map.size > this.maxSize) {
      const oldest = this.map.keys().next().value!;
      this.map.delete(oldest);
    }
  }

  has(key: K): boolean { return this.map.has(key); }

  delete(key: K): boolean { return this.map.delete(key); }

  keys(): IterableIterator<K> { return this.map.keys(); }

  clear(): void { this.map.clear(); }

  get size(): number { return this.map.size; }
}

/** Max planets to keep cached. Each planet stores ~3 LOD entries + 1 cloud. */
const MAX_CACHED_PLANETS = 5;
// Each planet has up to 3 terrain entries (LOD 0/1/2) + 1 cloud = 4 keys.
const TERRAIN_CACHE_CAPACITY = MAX_CACHED_PLANETS * 3;
const CLOUD_CACHE_CAPACITY = MAX_CACHED_PLANETS;

// ==================== TYPES ====================

/**
 * Optional region for partial baking. When provided, only the visible
 * lat/lon range is computed at full density. The returned canvases cover
 * exactly this region — the caller composites them onto the base textures.
 */
export interface BakeRegion {
  latMin: number; latMax: number;
  lonMin: number; lonMax: number;
}

/** Canvas type that works in both main thread (HTMLCanvasElement) and Web Workers
 * (OffscreenCanvas). Both expose `getContext('2d')` and a compatible 2D API for
 * the operations we use (drawImage, createImageData, putImageData, gradients). */
export type AnyCanvas = HTMLCanvasElement | OffscreenCanvas;

/** Create an HTMLCanvasElement on the main thread, OffscreenCanvas in a worker. */
function makeCanvas2D(w: number, h: number): { canvas: AnyCanvas; ctx: CanvasRenderingContext2D } {
  if (typeof document === 'undefined') {
    const canvas = new OffscreenCanvas(w, h);
    // OffscreenCanvasRenderingContext2D shares the subset of methods we use
    // with CanvasRenderingContext2D — safe to cast for ergonomics.
    return { canvas, ctx: canvas.getContext('2d') as unknown as CanvasRenderingContext2D };
  }
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  return { canvas, ctx: canvas.getContext('2d')! };
}

/** Both the colour (albedo) and tangent-space normal map canvases. */
export interface TerrainBakeResult {
  color: AnyCanvas;
  normal: AnyCanvas;
}

const TEXTURE_W = 2048;
const TEXTURE_H = 1024;

// ==================== COLOUR PALETTES ====================

interface Palette {
  ocean: [number, number, number];
  shore: [number, number, number];
  low:   [number, number, number];
  mid:   [number, number, number];
  high:  [number, number, number];
  peak:  [number, number, number];
}

const PALETTES: Record<string, Palette> = {
  terran: {
    ocean: [22, 44, 72],
    shore: [34, 62, 88],
    low:   [52, 88, 58],
    mid:   [68, 102, 62],
    high:  [98, 88, 68],
    peak:  [170, 175, 180],
  },
  barren: {
    ocean: [85, 75, 65],
    shore: [100, 90, 78],
    low:   [130, 118, 100],
    mid:   [148, 132, 110],
    high:  [118, 108, 95],
    peak:  [160, 152, 140],
  },
  rocky: {
    ocean: [58, 52, 48],
    shore: [75, 68, 62],
    low:   [95, 88, 80],
    mid:   [110, 100, 88],
    high:  [88, 78, 72],
    peak:  [140, 135, 128],
  },
  desert: {
    ocean: [90, 68, 40],
    shore: [120, 90, 55],
    low:   [175, 145, 85],
    mid:   [190, 155, 90],
    high:  [145, 110, 70],
    peak:  [200, 180, 155],
  },
  ice: {
    ocean: [30, 50, 78],
    shore: [55, 80, 108],
    low:   [140, 170, 190],
    mid:   [170, 195, 210],
    high:  [200, 215, 225],
    peak:  [235, 240, 245],
  },
  volcanic: {
    ocean: [140, 45, 10],
    shore: [90, 30, 12],
    low:   [45, 30, 25],
    mid:   [60, 40, 30],
    high:  [50, 45, 42],
    peak:  [110, 100, 90],
  },
  gas: {
    ocean: [140, 120, 90],
    shore: [160, 140, 100],
    low:   [175, 155, 110],
    mid:   [190, 170, 125],
    high:  [170, 150, 115],
    peak:  [200, 185, 150],
  },
};

/**
 * Per-type elevation thresholds. Controls what fraction of the surface
 * is "ocean" vs land. Each array is [ocean→shore, shore→low, low→mid, mid→high]
 * as t values in [0, 1].
 */
const ELEVATION_STOPS: Record<string, [number, number, number, number]> = {
  terran:   [0.32, 0.42, 0.55, 0.70],
  barren:   [0.05, 0.15, 0.45, 0.72],
  rocky:    [0.08, 0.18, 0.48, 0.70],
  desert:   [0.05, 0.12, 0.50, 0.75],
  ice:      [0.25, 0.35, 0.55, 0.72],
  volcanic: [0.20, 0.30, 0.50, 0.68],
  gas:      [0.30, 0.40, 0.55, 0.70],
};

/**
 * Sea-level threshold in continent-noise space [-1, 1].
 * Higher value → less ocean. Simplex output is ~gaussian centered at 0.
 */
const SEA_THRESHOLDS: Record<string, number> = {
  terran:   -0.20,
  barren:    0.70,
  rocky:     0.60,
  desert:    0.75,
  ice:       0.05,
  volcanic:  0.15,
  gas:       0.00,
};

/** Domain warp amplitude for continent octave. 0 = no warping. */
const WARP_AMPS: Record<string, number> = {
  terran:   0.28,
  barren:   0.25,
  rocky:    0.30,
  desert:   0.20,
  ice:      0.35,
  volcanic: 0.35,
  gas:      0.00,
};

// ==================== BIOME SYSTEM (terran only) ====================

/**
 * Four-corner biome interpolation via bilinear on (temperature, humidity).
 * Temperature: 0 = cold (poles), 1 = hot (equator).
 * Humidity: 0 = dry, 1 = wet.
 */
const BIOME_CORNERS = {
  coldDry: { low: [120, 115, 100] as [number, number, number], mid: [100, 95, 85] as [number, number, number] },
  coldWet: { low: [35, 68, 45] as [number, number, number],    mid: [42, 78, 50] as [number, number, number] },
  hotDry:  { low: [170, 140, 80] as [number, number, number],  mid: [185, 150, 85] as [number, number, number] },
  hotWet:  { low: [30, 75, 38] as [number, number, number],    mid: [22, 60, 30] as [number, number, number] },
};

function biomeColor(
  temp: number, hum: number, band: 'low' | 'mid'
): [number, number, number] {
  const { coldDry, coldWet, hotDry, hotWet } = BIOME_CORNERS;
  const topRow = lerp3(coldDry[band], coldWet[band], hum);
  const botRow = lerp3(hotDry[band], hotWet[band], hum);
  return lerp3(topRow, botRow, temp);
}

// ==================== SLOPE / DIRT / ICE COLOURS ====================

/** Exposed rock colour per planet type — blended onto steep slopes. */
const SLOPE_ROCK: Record<string, [number, number, number]> = {
  terran:   [105, 95, 80],
  barren:   [90, 82, 72],
  rocky:    [72, 65, 60],
  desert:   [130, 100, 65],
  ice:      [160, 170, 178],
  volcanic: [40, 32, 28],
  gas:      [150, 130, 100],
};

const DIRT_COLOURS: Record<string, [number, number, number]> = {
  terran:   [120, 90, 55],
  barren:   [110, 95, 75],
  rocky:    [80, 72, 65],
  desert:   [160, 120, 70],
  ice:      [90, 100, 115],
  volcanic: [55, 35, 28],
  gas:      [150, 130, 100],
};

const SNOW: [number, number, number] = [235, 240, 245];

// ==================== HELPERS ====================

function getPalette(planetType: string): Palette {
  return PALETTES[planetType] ?? PALETTES.barren;
}

function getStops(planetType: string): [number, number, number, number] {
  return ELEVATION_STOPS[planetType] ?? ELEVATION_STOPS.barren;
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

function smoothstep(edge0: number, edge1: number, x: number): number {
  const t = clamp((x - edge0) / (edge1 - edge0), 0, 1);
  return t * t * (3 - 2 * t);
}

function latLonToUnit(latDeg: number, lonDeg: number) {
  const phi = (latDeg * Math.PI) / 180;
  const lambda = (lonDeg * Math.PI) / 180;
  const cosPhi = Math.cos(phi);
  return {
    x: cosPhi * Math.cos(lambda),
    y: cosPhi * Math.sin(lambda),
    z: Math.sin(phi),
  };
}

function lerp3(
  a: [number, number, number],
  b: [number, number, number],
  t: number
): [number, number, number] {
  return [
    a[0] + (b[0] - a[0]) * t,
    a[1] + (b[1] - a[1]) * t,
    a[2] + (b[2] - a[2]) * t,
  ];
}

/**
 * Ridged multifractal noise — sharp mountain ridges instead of smooth fBm.
 * Output range: [0, ~1.75] for 3 octaves. Normalize with / 1.75.
 */
function sampleRidged(
  noiseFn: NoiseFunction3D,
  x: number, y: number, z: number,
  baseFreq: number,
  octaves: number
): number {
  let sum = 0;
  let freq = baseFreq;
  let amp = 1.0;
  let prev = 1.0;
  for (let i = 0; i < octaves; i++) {
    let n = 1 - Math.abs(noiseFn(x * freq, y * freq, z * freq));
    n = n * n;
    n *= prev;
    sum += n * amp;
    prev = clamp(n * 2, 0, 1);
    freq *= 2;
    amp *= 0.5;
  }
  return sum;
}

// ==================== NOISE SETUP ====================

interface TerrainNoise {
  // Elevation octaves
  continent: NoiseFunction3D;
  biome: NoiseFunction3D;
  terrain: NoiseFunction3D;
  detail: NoiseFunction3D;
  microDetail: NoiseFunction3D;
  fineGrain: NoiseFunction3D;
  // Color variation
  colorJitter: NoiseFunction3D;
  dirtPatch: NoiseFunction3D;
  river: NoiseFunction3D;
  microColor: NoiseFunction3D;
  // Domain warping
  warpX: NoiseFunction3D;
  warpY: NoiseFunction3D;
  warpZ: NoiseFunction3D;
  // Biome fields (terran)
  temperature: NoiseFunction3D;
  humidity: NoiseFunction3D;
}

function createTerrainNoise(profileSeed: number): TerrainNoise {
  return {
    continent:   createNoise3D(seedrandom(`terrain:continent:${profileSeed}`)),
    biome:       createNoise3D(seedrandom(`terrain:biome:${profileSeed}`)),
    terrain:     createNoise3D(seedrandom(`terrain:terrain:${profileSeed}`)),
    detail:      createNoise3D(seedrandom(`terrain:detail:${profileSeed}`)),
    microDetail: createNoise3D(seedrandom(`terrain:micro:${profileSeed}`)),
    fineGrain:   createNoise3D(seedrandom(`terrain:fine:${profileSeed}`)),
    colorJitter: createNoise3D(seedrandom(`terrain:cjitter:${profileSeed}`)),
    dirtPatch:   createNoise3D(seedrandom(`terrain:dirt:${profileSeed}`)),
    river:       createNoise3D(seedrandom(`terrain:river:${profileSeed}`)),
    microColor:  createNoise3D(seedrandom(`terrain:mcolor:${profileSeed}`)),
    warpX:       createNoise3D(seedrandom(`terrain:warpX:${profileSeed}`)),
    warpY:       createNoise3D(seedrandom(`terrain:warpY:${profileSeed}`)),
    warpZ:       createNoise3D(seedrandom(`terrain:warpZ:${profileSeed}`)),
    temperature: createNoise3D(seedrandom(`terrain:temp:${profileSeed}`)),
    humidity:    createNoise3D(seedrandom(`terrain:humid:${profileSeed}`)),
  };
}

// ==================== ELEVATION ====================

/**
 * Gas giant elevation — latitude-based bands with turbulence + storms.
 * Uses sin(lat) instead of continent/terrain noise.
 */
function sampleElevationGas(
  noise: TerrainNoise,
  x: number, y: number, z: number,
  cd: number,
  lod: number
): number {
  // Latitude from unit sphere (z = sin(lat) in baker convention)
  const lat = Math.asin(clamp(z, -1, 1));

  // Base bands: sinusoidal latitude stripes
  const bandFreq = 6 + cd * 4;

  // Turbulence: domain-warp the bands for organic swirls
  const turbFreq = 3 + cd * 3;
  const turb = noise.continent(x * turbFreq, y * turbFreq, z * turbFreq) * 0.3;
  const warpedBand = Math.sin((lat + turb) * bandFreq);

  // East-west variation within bands
  const ewFreq = 4 + cd * 2;
  const ew = noise.biome(x * ewFreq, y * ewFreq, z * ewFreq) * 0.15;

  // Storm spots: ridged noise peaks
  const stormFreq = 8 + cd * 6;
  const stormRaw = noise.terrain(x * stormFreq, y * stormFreq, z * stormFreq);
  const storm = Math.max(0, Math.abs(stormRaw) - 0.7) / 0.3;

  let sum = warpedBand * 0.5 + ew + storm * 0.3;

  if (lod >= 1) {
    const detFreq = 12 + cd * 8;
    sum += noise.detail(x * detFreq, y * detFreq, z * detFreq) * 0.08;
  }
  if (lod >= 2) {
    const fineFreq = 30 + cd * 20;
    sum += noise.fineGrain(x * fineFreq, y * fineFreq, z * fineFreq) * 0.04;
  }

  return clamp(sum, -1, 1);
}

/**
 * Solid planet elevation: domain warping + ridged multifractal + continent mask.
 * Output: [-1, 1] aligned with ELEVATION_STOPS for elevationToColour().
 */
function sampleElevation(
  noise: TerrainNoise,
  x: number, y: number, z: number,
  clusterDensity: number,
  planetType: string,
  lod: number = 0
): number {
  const cd = clusterDensity;
  if (planetType === 'gas') return sampleElevationGas(noise, x, y, z, cd, lod);

  const stops = getStops(planetType);
  const shoreStop = stops[1];

  // === Domain-warped continent ===
  const contFreq = 0.9 + cd * 0.5;
  const warpAmp = WARP_AMPS[planetType] ?? 0;
  let cx = x, cy = y, cz = z;
  if (warpAmp > 0) {
    const wf = contFreq * 0.7;
    cx += noise.warpX(x * wf, y * wf, z * wf) * warpAmp;
    cy += noise.warpY(x * wf, y * wf, z * wf) * warpAmp;
    cz += noise.warpZ(x * wf, y * wf, z * wf) * warpAmp;
  }
  const continentRaw = noise.continent(cx * contFreq, cy * contFreq, cz * contFreq);

  // === Continent mask ===
  const seaThresh = SEA_THRESHOLDS[planetType] ?? 0;
  const coastWidth = 0.08;
  const mask = smoothstep(seaThresh - coastWidth, seaThresh + coastWidth, continentRaw);

  // === Ocean: flat depth ===
  const oceanDepthNorm = clamp((continentRaw + 1) / (seaThresh + 1), 0, 1);
  const oceanT = oceanDepthNorm * shoreStop;

  // === Land elevation ===
  const biomeFreq = 2.5 + cd * 2.5;
  const biome = noise.biome(x * biomeFreq, y * biomeFreq, z * biomeFreq);
  const terrFreq = 6 + cd * 8;
  const ridgedNorm = sampleRidged(noise.terrain, x, y, z, terrFreq, 3) / 1.75;
  const detFreq = 18 + cd * 17;
  const detail = noise.detail(x * detFreq, y * detFreq, z * detFreq);

  const aboveSea = clamp((continentRaw - seaThresh) / (1 - seaThresh), 0, 1);
  const detailFade = Math.sqrt(clamp(aboveSea / 0.3, 0, 1));
  const landFeatures =
    aboveSea * 0.40 +
    (biome * 0.15 + ridgedNorm * 0.35 + detail * 0.10) * detailFade;
  const landClamped = clamp(landFeatures, 0, 1);
  const landT = shoreStop + landClamped * (1 - shoreStop);

  // === Blend ===
  let t = oceanT * (1 - mask) + landT * mask;

  if (lod >= 1) {
    const microFreq = 50 + cd * 30;
    t += noise.microDetail(x * microFreq, y * microFreq, z * microFreq) * 0.02 * mask;
  }
  if (lod >= 2) {
    const fineFreq = 120 + cd * 80;
    t += noise.fineGrain(x * fineFreq, y * fineFreq, z * fineFreq) * 0.012 * mask;
  }

  return clamp(t * 2 - 1, -1, 1);
}

// ==================== COLOUR ====================

function elevationToColour(
  elev: number,
  palette: Palette,
  stops: [number, number, number, number]
): [number, number, number] {
  const t = (elev + 1) * 0.5;
  const [s0, s1, s2, s3] = stops;
  if (t < s0) return lerp3(palette.ocean, palette.shore, t / s0);
  if (t < s1) return lerp3(palette.shore, palette.low, (t - s0) / (s1 - s0));
  if (t < s2) return lerp3(palette.low, palette.mid, (t - s1) / (s2 - s1));
  if (t < s3) return lerp3(palette.mid, palette.high, (t - s2) / (s3 - s2));
  return lerp3(palette.high, palette.peak, Math.min(1, (t - s3) / (1 - s3)));
}

function elevBand(elev: number, stops: [number, number, number, number]): 'water' | 'land' | 'high' {
  const t = (elev + 1) * 0.5;
  if (t < stops[1]) return 'water';
  if (t < stops[3]) return 'land';
  return 'high';
}

// ==================== TEXTURE BAKING ====================

const terrainCache = new LRUMap<string, TerrainBakeResult>(TERRAIN_CACHE_CAPACITY);

/**
 * Bake terrain colour + normal map canvases.
 *
 * Two-pass approach:
 *   Pass 1 — elevation buffer (all noise computation)
 *   Pass 2 — colour (palette + biome + slope + dirt + rivers + ice caps)
 *             + normal map (from elevation gradient)
 *
 * When `region` is omitted: full globe, cached per seed+width.
 * When `region` is provided: partial bake at same pixel density, not cached.
 */
export function bakeTerrainCanvases(
  profile: PlanetProfileWire,
  planetType: string,
  w: number = TEXTURE_W,
  h: number = TEXTURE_H,
  region?: BakeRegion
): TerrainBakeResult {
  if (!region) {
    const cacheKey = `${profile.profileSeed}:${w}`;
    const cached = terrainCache.get(cacheKey);
    if (cached) return cached;
  }

  // Pixel bounds
  const latMin = region?.latMin ?? -90;
  const latMax = region?.latMax ?? 90;
  const lonMin = region?.lonMin ?? -180;
  const lonMax = region?.lonMax ?? 180;
  const pxPerDegLon = w / 360;
  const pxPerDegLat = h / 180;
  const cw = region ? Math.ceil((lonMax - lonMin) * pxPerDegLon) : w;
  const ch = region ? Math.ceil((latMax - latMin) * pxPerDegLat) : h;

  // Create canvases (HTMLCanvasElement on main thread, OffscreenCanvas in worker)
  const { canvas: colorCanvas, ctx: colorCtx } = makeCanvas2D(cw, ch);
  const colorImg = colorCtx.createImageData(cw, ch);
  const cData = colorImg.data;

  const { canvas: normalCanvas, ctx: normalCtx } = makeCanvas2D(cw, ch);
  const normalImg = normalCtx.createImageData(cw, ch);
  const nData = normalImg.data;

  const noise = createTerrainNoise(profile.profileSeed);
  const palette = getPalette(planetType);
  const stops = getStops(planetType);
  const dirtCol = DIRT_COLOURS[planetType] ?? DIRT_COLOURS.barren;
  const rockCol = SLOPE_ROCK[planetType] ?? SLOPE_ROCK.barren;
  const lod = w >= 4096 ? 2 : w >= 2048 ? 1 : 0;
  const cd = profile.clusterDensity;
  const isTerran = planetType === 'terran';
  const hasIceCaps = planetType === 'terran' || planetType === 'ice';

  // ============ Pass 1: elevation buffer ============
  const elevBuf = new Float32Array(cw * ch);
  for (let py = 0; py < ch; py++) {
    const lat = latMax - (py / ch) * (latMax - latMin);
    for (let px = 0; px < cw; px++) {
      const lon = lonMin + (px / cw) * (lonMax - lonMin);
      const { x, y, z } = latLonToUnit(lat, lon);
      elevBuf[py * cw + px] = sampleElevation(noise, x, y, z, cd, planetType, lod);
    }
  }

  // Slope scale (normalised for texture resolution)
  const slopeScale = cw * 0.03;
  const normalStrength = 6.0;

  // Mutable palette for biome overrides
  const biomePal: Palette = isTerran ? { ...palette } : palette;

  // ============ Pass 2: colour + slope + biome + ice + normal ============
  for (let py = 0; py < ch; py++) {
    const lat = latMax - (py / ch) * (latMax - latMin);
    for (let px = 0; px < cw; px++) {
      const lon = lonMin + (px / cw) * (lonMax - lonMin);
      const { x, y, z } = latLonToUnit(lat, lon);
      const bi = py * cw + px;
      const elev = elevBuf[bi];
      const band = elevBand(elev, stops);

      // --- Slope from elevation gradient ---
      const elevR = px < cw - 1 ? elevBuf[bi + 1] : elev;
      const elevD = py < ch - 1 ? elevBuf[bi + cw] : elev;
      const dx = elevR - elev;
      const dy = elevD - elev;
      const slope = clamp(Math.sqrt(dx * dx + dy * dy) * slopeScale, 0, 1);

      // --- Biome override for terran land ---
      if (isTerran && band !== 'water') {
        const tempFreq = 1.8;
        const tempNoise = noise.temperature(x * tempFreq, y * tempFreq, z * tempFreq);
        const latNorm = 1 - Math.abs(lat) / 90;
        const temp = clamp(latNorm * 0.65 + tempNoise * 0.35, 0, 1);
        const humFreq = 2.5;
        const humNoise = noise.humidity(x * humFreq, y * humFreq, z * humFreq);
        const hum = clamp(humNoise * 0.5 + 0.5, 0, 1);
        biomePal.low = biomeColor(temp, hum, 'low');
        biomePal.mid = biomeColor(temp, hum, 'mid');
      }

      let [r, g, b2] = elevationToColour(
        elev, isTerran && band !== 'water' ? biomePal : palette, stops
      );

      // --- Slope-based rock blending ---
      if (band !== 'water' && slope > 0.15) {
        const sb = smoothstep(0.15, 0.55, slope);
        r  = r  + (rockCol[0] - r)  * sb;
        g  = g  + (rockCol[1] - g)  * sb;
        b2 = b2 + (rockCol[2] - b2) * sb;
      }

      // --- Color jitter (LOD 1+) ---
      if (lod >= 1) {
        const jitFreq = 8 + cd * 6;
        const jitter = noise.colorJitter(x * jitFreq, y * jitFreq, z * jitFreq);
        const ja = 1.0 + jitter * 0.12;
        r *= ja; g *= ja; b2 *= ja;
      }

      // --- Dirt patches (LOD 1+, land) ---
      if (band === 'land' && lod >= 1) {
        const dirtFreq = 14 + cd * 10;
        const dv = noise.dirtPatch(x * dirtFreq, y * dirtFreq, z * dirtFreq);
        if (dv > 0.3) {
          const ds = (dv - 0.3) / 0.7;
          r  = r  + (dirtCol[0] - r)  * ds * 0.6;
          g  = g  + (dirtCol[1] - g)  * ds * 0.6;
          b2 = b2 + (dirtCol[2] - b2) * ds * 0.6;
        }
      }

      // --- River lines (LOD 2, land) ---
      // Low frequency (2–4) = few major rivers. High threshold (0.93) = thin lines.
      if (band === 'land' && lod >= 2) {
        const rivFreq = 2 + cd * 2;
        const rivRaw = noise.river(x * rivFreq, y * rivFreq, z * rivFreq);
        const ridge = 1.0 - Math.abs(rivRaw);
        if (ridge > 0.93) {
          const rs = (ridge - 0.93) / 0.07;
          const df = 1.0 - rs * 0.5;
          r  = r  * df * 0.7;
          g  = g  * df * 0.85;
          b2 = b2 * df + rs * 25;
        }
      }

      // --- Micro color grain (LOD 2, non-water) ---
      if (band !== 'water' && lod >= 2) {
        const mcFreq = 80 + cd * 60;
        const mc = noise.microColor(x * mcFreq, y * mcFreq, z * mcFreq);
        r += mc * 8; g += mc * 10; b2 += mc * 5;
      }

      // --- Polar ice caps ---
      if (hasIceCaps && band !== 'water') {
        const absLat = Math.abs(lat);
        const capStart = planetType === 'ice' ? 55 : 72;
        const capFull  = planetType === 'ice' ? 75 : 85;
        if (absLat > capStart) {
          const cb = smoothstep(capStart, capFull, absLat);
          r  = r  + (SNOW[0] - r)  * cb;
          g  = g  + (SNOW[1] - g)  * cb;
          b2 = b2 + (SNOW[2] - b2) * cb;
        }
      }

      // Write colour pixel
      const idx = bi * 4;
      cData[idx]     = Math.max(0, Math.min(255, r));
      cData[idx + 1] = Math.max(0, Math.min(255, g));
      cData[idx + 2] = Math.max(0, Math.min(255, b2));
      cData[idx + 3] = 255;

      // --- Normal map from elevation gradient ---
      const nx = -dx * normalStrength;
      const ny = dy * normalStrength;
      const nz = 1.0;
      const nLen = Math.sqrt(nx * nx + ny * ny + nz * nz);
      nData[idx]     = ((nx / nLen) * 0.5 + 0.5) * 255;
      nData[idx + 1] = ((ny / nLen) * 0.5 + 0.5) * 255;
      nData[idx + 2] = ((nz / nLen) * 0.5 + 0.5) * 255;
      nData[idx + 3] = 255;
    }
  }

  colorCtx.putImageData(colorImg, 0, 0);
  normalCtx.putImageData(normalImg, 0, 0);
  const result: TerrainBakeResult = { color: colorCanvas, normal: normalCanvas };

  if (!region) {
    const cacheKey = `${profile.profileSeed}:${w}`;
    terrainCache.set(cacheKey, result);
  }
  return result;
}

/** Clear cached terrain for a seed (e.g. on panel close). */
export function clearTerrainCache(profileSeed?: number): void {
  if (profileSeed != null) {
    for (const key of terrainCache.keys()) {
      if (key.startsWith(`${profileSeed}:`)) terrainCache.delete(key);
    }
  } else {
    terrainCache.clear();
  }
}

// ==================== CLOUD TEXTURE ====================

const cloudCache = new LRUMap<string, HTMLCanvasElement>(CLOUD_CACHE_CAPACITY);

/**
 * Bake a cloud layer texture — white wisps with transparent gaps.
 * Two octaves of low-frequency simplex noise. Alpha-only (white colour).
 */
export function bakeCloudCanvas(
  profileSeed: number,
  w: number = 1024,
  h: number = 512
): HTMLCanvasElement {
  const key = `cloud:${profileSeed}:${w}`;
  const cached = cloudCache.get(key);
  if (cached) return cached;

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d')!;

  const noiseLo = createNoise3D(seedrandom(`cloud:lo:${profileSeed}`));
  const noiseHi = createNoise3D(seedrandom(`cloud:hi:${profileSeed}`));

  const imgData = ctx.createImageData(w, h);
  const d = imgData.data;

  for (let py = 0; py < h; py++) {
    const lat = 90 - (py / h) * 180;
    for (let px = 0; px < w; px++) {
      const lon = -180 + (px / w) * 360;
      const { x, y, z } = latLonToUnit(lat, lon);

      const lo = noiseLo(x * 2.5, y * 2.5, z * 2.5);
      const hi = noiseHi(x * 6, y * 6, z * 6) * 0.3;
      const cloud = lo + hi;

      const idx = (py * w + px) * 4;
      d[idx] = d[idx + 1] = d[idx + 2] = 255;
      d[idx + 3] = cloud > 0.05 ? smoothstep(0.05, 0.55, cloud) * 160 : 0;
    }
  }

  ctx.putImageData(imgData, 0, 0);
  cloudCache.set(key, canvas);
  return canvas;
}

export function clearCloudCache(profileSeed?: number): void {
  if (profileSeed != null) {
    for (const key of cloudCache.keys()) {
      if (key.includes(`:${profileSeed}:`)) cloudCache.delete(key);
    }
  } else {
    cloudCache.clear();
  }
}
